import { CSV_DATA_TYPE, formatUTCDateShort } from '@demandproai/shared';
import Constants from '../../../Constants';
import HealthStatCard from './components/HealthStatCard'; 

const getLast12MonthsTable = () => {
    const months = [];
    const now = new Date();
    
    for (let i = 11; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i);
        const monthName = date.toLocaleString('en-US', { month: 'short' });
        const monthNumber = date.getMonth() + 1;
        const year = date.getFullYear();
        
        months.push({
            label: monthName,
            monthNumber,
            year,
            accessor: `${year}_${monthNumber}`
        });
    }
    return months;
};

const transformDemandUsageData = (data) => {
    const transformedData = data.map(company => {
        const transformed = {
            customer: company.customer,
            tier: company.tier,
            total: company.total,
            avg6mo: company.avg6mo,
            momPercent: company.momPercent,
            utilization: company.utilization
        };
        
        company.months.forEach(monthData => {
            const accessor = `${monthData.year}_${monthData.month}`;
            transformed[accessor] = monthData.count;
        });
        return transformed;
    });
    
    return { tableData: transformedData };
}

export const REPORT_CONFIGS = [
    {
        title: 'Customers Exceeding Subscription Credits',
        description: 'Identify customers who regularly exceed their monthly credit limit for upsell targeting.',
        columns: () => [
            { header: "Customer Name", accessor: "customerName" },
            { header: "Tier", accessor: "tier" },
            { header: "Type", accessor: "type" },
            { header: "Credit Limit", accessor: "creditLimit" },
            { header: "Credits Used (MTD)", accessor: "creditsUsed" },
            { header: "Over Limit", accessor: "overLimit" },
            { header: "% Over", accessor: "percentOver" }
        ],
        apiEndpoint: () => Constants.ApiUrl.super.reports.exceedingCredits,
        csvType: CSV_DATA_TYPE.EXCEEDING_CREDITS,
    },

    {
        title: 'Inactive Customers',
        description: 'Identify customers who have created no demands or have been inactive for an extended period.',
        columns: () => [
            { header: "Customer Name", accessor: "companyName" },
            { header: "Start Date", accessor: "startedOn", render: (row) => formatUTCDateShort(row.startedOn) },
            { header: "Last Demand", accessor: "lastDemandCreatedDate", render: (row) => formatUTCDateShort(row.lastDemandCreatedDate) },
            { header: "Total Demands", accessor: "totalDemands" },
            { header: "Tier", accessor: "tier" },
            { header: "Days Inactive", accessor: "daysSinceLastActivity", render: (row) => (`${row.daysSinceLastActivity || 0} days`) }
        ],
        apiEndpoint: () => `${Constants.ApiUrl.super.dashboard.demandEligibility}?startDate=${new Date(new Date().getFullYear(), 0, 1)}&endDate=${new Date()}`,
        csvType: CSV_DATA_TYPE.INACTIVE_CUSTOMERS,
        transformData : (data) => ({ tableData: data.inactiveCompaniesWithMostRecentCaseData}),

    },
    {
        title: 'Customer Demand Usage Over Time',
        description: 'Track total and monthly demand creation trends across all customers.',
        tableClassName: 'w-max',
        columns: () => [
            { header: "Customer Name", accessor: "customer" },
            { header: "Tier", accessor: "tier" },
            { header: "Total", accessor: "total" },
            ...getLast12MonthsTable().map(month => ({
                header: <>{month.label}-{month.year}</>,
                accessor: month.accessor,
                render: (row) => row[month.accessor] || 0
            })),
            { header: "Avg (6mo)", accessor: "avg6mo" },
            { header: "MoM %", accessor: "momPercent" },
            { header: "Utilization", accessor: "utilization" },
        ],
        apiEndpoint: () => Constants.ApiUrl.super.reports.demandUsage,
        apiPayLoad: () => {
            const endDate = new Date();
            const startDate = new Date(endDate.getFullYear(), endDate.getMonth() - 11, endDate.getDate());
            return { startDate, endDate };
        },
        csvType: CSV_DATA_TYPE.OVER_USAGE_DEMAND,
        transformData: transformDemandUsageData
    },
    {
        title: 'Subscription Health Overview',
        description: 'Provide a snapshot of customer health, engagement, and renewal readiness.',
        columns: () => [
            { header: "Customer Name", accessor: "customerName" },
            { header: "Tier", accessor: "tier" },
            { header: "Health", accessor: "health" },
            { header: "Start Date", accessor: "subscriptionStartDate", render: (row) => formatUTCDateShort(row.subscriptionStartDate) },
            { header: "Renewal", accessor: "renewalDate", render: (row) => formatUTCDateShort(row.renewalDate) },
            { header: "Days Left", accessor: "daysLeft" },
            { header: "Credit", accessor: "credits" },
            { header: "Used (MTD)", accessor: "used" },
            { header: "Avg/Month", accessor: "avgPerMonth" },
            { header: "Active Months", accessor: "activeMonths" },
            { header: "Utilization",accessor: "utilization"}
        ],
        apiEndpoint: () => Constants.ApiUrl.super.reports.subscriptionHealth,
        csvType: CSV_DATA_TYPE.HEALTH_OVERVIEW,
        transformData : (data) => ({ tableData: data.companies,additionalData: { healthSummary: data.healthSummary }}),
        additionalComponent: HealthStatCard
    }
];